import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalculatorIcon, Gauge } from "lucide-react";
import { Calculator } from "@/components/Calculator";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { deviceList } from "@/data/devices";

// Allgemeiner Watt-basierter Rechner für beliebige Geräte (PRD §16, §9).
const genericDevice = {
  key: "allgemein",
  name: "Gerät",
  mode: "watt",
  rangeUnit: "W",
  ranges: { low: 50, typical: 150, high: 500 },
  price: 0.35,
  exact: {
    fields: [
      { id: "watt", label: "Leistung", unit: "W", default: 150, min: 1, max: 5000, step: 10 },
      { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 2, min: 0.25, max: 24, step: 0.25 },
      { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
    ],
  },
  estimate: {
    profileFields: [
      { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 2, min: 0.25, max: 24, step: 0.25 },
      { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
    ],
    assumptionText:
      "Ohne Gerätetyp rechnen wir mit einer breiten Standardspanne. Für genauere Ergebnisse nutze die passende Geräteseite oder trage die Wattzahl ein.",
  },
};

const FAQ = [
  { q: "Wie berechne ich die Stromkosten eines Geräts?", a: "Leistung in Watt mal Nutzungsstunden geteilt durch 1.000 ergibt den Verbrauch in Kilowattstunden. Multipliziert mit dem Strompreis erhältst du die Kosten." },
  { q: "Wo finde ich die Wattzahl meines Geräts?", a: "Meist auf einem Typenschild am Gerät, im Handbuch oder auf der Herstellerseite. Wenn du sie nicht findest, hilft ein Strommessgerät oder eine der gerätespezifischen Seiten mit Schätzwerten." },
  { q: "Welcher Strompreis ist realistisch?", a: "Als Orientierung sind 0,35 € pro Kilowattstunde voreingestellt. Deinen tatsächlichen Preis findest du auf der Stromrechnung. Trage ihn für genauere Ergebnisse einfach ein." },
  { q: "Warum zeigt der Rechner eine Spanne?", a: "Viele Geräte laufen nicht konstant auf voller Leistung. Eine Spanne von niedrig bis hoch ist ehrlicher als ein einzelner, scheinbar exakter Wert." },
];

export default function StromkostenrechnerPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Stromkostenrechner",
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      url: `${origin}/stromkostenrechner`,
      inLanguage: "de-DE",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      description: "Allgemeiner Stromkostenrechner: berechne die Stromkosten beliebiger Geräte pro Tag, Monat und Jahr.",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <div>
      <Seo
        title="Stromkostenrechner: Stromkosten beliebiger Geräte berechnen | Gerätekosten"
        description="Berechne die Stromkosten eines beliebigen Geräts pro Tag, Monat und Jahr. Mit Wattzahl oder Schätzung. Kostenlos, ohne Anmeldung, mit Spanne."
      canonical={origin ? `${origin}/stromkostenrechner` : undefined}
      />
      <JsonLd data={jsonLd} />

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.08), rgba(249,115,22,0.06), rgba(251,250,247,0))", height: "50%" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6 relative">
          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-8">
            <div>
              <Badge variant="secondary" className="mb-3">Allgemein</Badge>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Stromkostenrechner</h1>
              <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">
                Berechne, was ein beliebiges Gerät pro Tag, Monat und Jahr an Strom kostet. Gib die Wattzahl ein oder lass
                grob schätzen. Für genauere Ergebnisse nutze die passende Geräteseite.
              </p>
              <div className="mt-6"><Calculator device={genericDevice} defaultMode="exact" /></div>
            </div>
            <div className="hidden lg:block"><AdSlot variant="rail" /></div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:hidden mt-4"><AdSlot variant="inline" /></div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Card className="p-5">
          <div className="flex items-center gap-2 font-heading font-semibold">
            <CalculatorIcon className="h-5 w-5 text-primary" /> Die Formel
          </div>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Verbrauch in Kilowattstunden ist gleich Leistung in Watt mal Stunden geteilt durch 1.000. Die Kosten ergeben
            sich aus Verbrauch mal Strompreis. Ein Gerät mit 150 Watt, das 2 Stunden täglich läuft, verbraucht rund 0,3
            Kilowattstunden am Tag, also etwa 0,11 € bei 0,35 € pro Kilowattstunde.
          </p>
        </Card>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6"><AdSlot variant="inline" /></div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-xl font-semibold tracking-tight mb-4">Direkt zum passenden Gerät</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {deviceList.map((d) => (
            <Link
              key={d.key}
              to={`/${d.slug}`}
              className="group flex items-center justify-between rounded-xl border p-4 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <span className="font-medium">{d.name} Stromkosten</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <h2 className="text-2xl font-semibold tracking-tight mb-4">Häufige Fragen</h2>
        <Faq items={FAQ} />
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6">
        <Card className="p-5 bg-muted/30">
          <div className="flex items-start gap-3">
            <Gauge className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Die Werte sind Schätzungen. Den genauen Verbrauch misst du mit einem Strommessgerät zwischen Steckdose und Gerät.
            </p>
          </div>
        </Card>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10"><AdSlot variant="inline" /></div>
    </div>
  );
}
